import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";

const STATUS_LABELS = {
  pending: { label: "Inget kort registrerat", tone: "warn" },
  trialing: { label: "Gratis prövotid pågår", tone: "ok" },
  active: { label: "Aktiv prenumeration", tone: "ok" },
  past_due: { label: "Betalning misslyckades", tone: "bad" },
  unpaid: { label: "Obetald — spärrad", tone: "bad" },
  canceled: { label: "Uppsagd", tone: "bad" },
  grandfathered: { label: "Permanent access", tone: "ok" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function Billing() {
  const { user, subscription, refreshSubscription, logout } = useAuth();
  const { companyName } = useBranding();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const status = subscription?.subscription_status || "pending";
  const meta = STATUS_LABELS[status] || { label: status, tone: "warn" };

  const startCheckout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/stripe/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte starta betalning");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/stripe/portal", {
        return_url: `${window.location.origin}/billing`,
      });
      window.location.href = r.data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte öppna kundportalen");
      setBusy(false);
    }
  };

  const cancelSubscription = async () => {
    if (busy) return;
    if (!window.confirm("Säg upp abonnemanget? Ni behåller access fram till nästa förnyelsedatum.")) return;
    setBusy(true);
    try {
      await api.post("/stripe/cancel-subscription");
      toast.success("Abonnemanget är uppsagt vid periodens slut");
      await refreshSubscription();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte säga upp abonnemanget");
    } finally {
      setBusy(false);
    }
  };

  const reactivateSubscription = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/stripe/reactivate-subscription");
      toast.success("Abonnemanget förnyas som vanligt igen");
      await refreshSubscription();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte återaktivera abonnemanget");
    } finally {
      setBusy(false);
    }
  };

  const isAdmin = user.role === "admin";
  const showCheckout = isAdmin && ["pending", "past_due", "canceled", "unpaid"].includes(status);
  const showPortal =
    isAdmin && subscription?.has_customer && ["trialing", "active", "past_due"].includes(status);
  const showCancel =
    isAdmin
    && subscription?.has_subscription
    && ["trialing", "active"].includes(status)
    && !subscription?.cancel_at_period_end;
  const showReactivate =
    isAdmin
    && subscription?.has_subscription
    && ["trialing", "active"].includes(status)
    && subscription?.cancel_at_period_end;

  return (
    <div data-testid="billing-page" className="min-h-screen bg-app">
      <div className="max-w-3xl mx-auto px-6 py-14">
        <div className="mb-2 text-xs tracking-[0.2em] uppercase text-text-muted">
          {companyName}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-text-primary leading-[1.05]">
          Fakturering & <span className="text-brand">abonnemang.</span>
        </h1>
        <p className="mt-4 text-text-muted text-sm max-w-lg">
          Hantera ert abonnemang, kort och kvitton. Paketpris per intervall:
          1–5 platser = 750&nbsp;kr/mån, 6–10 platser = 1&nbsp;125&nbsp;kr/mån, 11+ platser =
          1&nbsp;500&nbsp;kr/mån (obegränsat). Alla priser inkl. 25 % moms.
          Faktureras årsvis och momsen redovisas separat på fakturan.
        </p>

        <div className="mt-10 bg-surface border border-app rounded-2xl p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs text-text-muted uppercase tracking-widest">Status</div>
              <div
                data-testid="billing-status-label"
                className={
                  "mt-1 text-xl font-heading " +
                  (meta.tone === "ok"
                    ? "text-brand"
                    : meta.tone === "bad"
                    ? "text-red-400"
                    : "text-amber-400")
                }
              >
                {meta.label}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-text-muted uppercase tracking-widest">Aktiva platser</div>
              <div data-testid="billing-seats" className="mt-1 text-xl font-heading text-text-primary">
                {subscription?.seats ?? "—"}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-text-muted text-xs uppercase tracking-widest">
                Prövoperiod slutar
              </div>
              <div className="mt-1 text-text-primary">{fmtDate(subscription?.trial_ends_at)}</div>
            </div>
            <div>
              <div className="text-text-muted text-xs uppercase tracking-widest">
                Nästa förnyelse
              </div>
              <div className="mt-1 text-text-primary">
                {fmtDate(subscription?.current_period_end)}
              </div>
            </div>
          </div>

          {subscription?.cancel_at_period_end && (
            <div className="mt-4 bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm rounded-lg px-4 py-3">
              Abonnemanget är uppsagt och löper ut vid nästa förnyelsedatum.
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {showCheckout && (
              <button
                data-testid="billing-checkout-button"
                onClick={startCheckout}
                disabled={busy}
                className="tk-btn-primary"
              >
                {status === "pending"
                  ? "Registrera kort & starta prövoperiod"
                  : "Åtgärda betalning"}
              </button>
            )}
            {showPortal && (
              <button
                data-testid="billing-portal-button"
                onClick={openPortal}
                disabled={busy}
                className="tk-btn-secondary"
              >
                Öppna kundportal
              </button>
            )}
            {showCancel && (
              <button
                data-testid="billing-cancel-button"
                onClick={cancelSubscription}
                disabled={busy}
                className="tk-btn-outline"
              >
                Säg upp abonnemanget
              </button>
            )}
            {showReactivate && (
              <button
                data-testid="billing-reactivate-button"
                onClick={reactivateSubscription}
                disabled={busy}
                className="tk-btn-secondary"
              >
                Ångra uppsägning
              </button>
            )}
            {!isAdmin && status !== "grandfathered" && (
              <p className="text-text-muted text-sm">
                Kontakta din administratör för att aktivera abonnemanget.
              </p>
            )}
            {status === "grandfathered" && (
              <Link to="/" className="tk-btn-primary" data-testid="billing-back-app">
                Till appen
              </Link>
            )}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between text-xs text-text-muted">
          <Link to="/" className="hover:text-brand" data-testid="billing-back-link">
            ← Tillbaka
          </Link>
          <button onClick={logout} className="hover:text-brand" data-testid="billing-logout-link">
            Logga ut
          </button>
        </div>
      </div>
    </div>
  );
}
